import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import logo from "@/assets/logo.png";

const GOLD = "#B8977E";
const TEXT_LIGHT = "#f5ede8";
const TEXT_MUTED = "#a0a0a0";
const BORDER = "#3a3a3a";

const openCookieBanner = (e) => {
  e.preventDefault();
  try { localStorage.removeItem("cookie_consent"); } catch (_) { /* ignore */ }
  window.dispatchEvent(new Event("reopen-cookie-banner"));
};

const columnTitleStyle = {
  fontFamily: "'Playfair Display',serif",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: GOLD,
  textTransform: "uppercase",
  letterSpacing: "0.15em",
  marginBottom: "1rem",
};

const linkStyle = {
  color: TEXT_LIGHT,
  textDecoration: "none",
  fontFamily: "'DM Sans',sans-serif",
  fontSize: "0.88rem",
  transition: "color .2s ease, text-decoration .2s ease",
  display: "inline-block",
};

const contactRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.55rem",
  marginBottom: "0.6rem",
  color: TEXT_LIGHT,
  fontFamily: "'DM Sans',sans-serif",
  fontSize: "0.88rem",
};

const socialLinkStyle = {
  color: GOLD,
  transition: "transform .2s ease, opacity .2s ease",
  display: "inline-flex",
};

const NAV_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/galerie", label: "Galerie" },
  { to: "/booking", label: "Réserver" },
  { to: "/landing", label: "Devis gratuit" },
];

const LEGAL_LINKS = [
  { to: "/mentions-legales", label: "Mentions légales", testid: "footer-legal-link" },
  { to: "/cgv", label: "CGV", testid: "footer-terms-link" },
  { to: "/politique-confidentialite", label: "Politique de confidentialité", testid: "footer-privacy-link" },
  { to: "/politique-cookies", label: "Politique cookies", testid: "footer-cookies-link" },
];

export const Footer = () => (
  <footer
    data-testid="site-footer"
    style={{
      background: "#1A1A1A",
      color: "#fff",
      padding: "4rem 2rem 2rem",
      fontFamily: "'DM Sans',sans-serif",
      marginTop: "auto",
    }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div
        className="footer-grid"
        style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: "3rem" }}
      >
        {/* Col 1 — Brand */}
        <div>
          <img
            src={logo}
            alt="SelfieBooth Event"
            style={{ height: 56, width: "auto", filter: "brightness(0) invert(1)", marginBottom: "1rem" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <p style={{ color: TEXT_MUTED, fontSize: "0.85rem", lineHeight: 1.65, margin: 0 }}>
            Location de photobooth haut de gamme en Île-de-France. Mariages, anniversaires, événements d&apos;entreprise.
          </p>
        </div>

        {/* Col 2 — Navigation */}
        <div>
          <p style={columnTitleStyle}>Navigation</p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {NAV_LINKS.map((l) => (
              <li key={l.to} style={{ marginBottom: "0.55rem" }}>
                <Link to={l.to} style={linkStyle} className="footer-link">{l.label}</Link>
              </li>
            ))}
            <li style={{ margin: "0.75rem 0", borderTop: `1px solid ${BORDER}` }} />
            {LEGAL_LINKS.map((l) => (
              <li key={l.to} style={{ marginBottom: "0.55rem" }}>
                <Link to={l.to} style={linkStyle} className="footer-link" data-testid={l.testid}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 — Contact */}
        <div>
          <p style={columnTitleStyle}>Contact</p>
          <a href="mailto:walid.societe.pro@gmail.com" style={{ ...contactRowStyle, textDecoration: "none" }} className="footer-link" data-testid="footer-email">
            <Mail style={{ width: 15, height: 15, color: GOLD, flexShrink: 0 }} />
            <span style={{ wordBreak: "break-word" }}>walid.societe.pro@gmail.com</span>
          </a>
          <a href="tel:+33679393429" style={{ ...contactRowStyle, textDecoration: "none" }} className="footer-link" data-testid="footer-phone">
            <Phone style={{ width: 15, height: 15, color: GOLD, flexShrink: 0 }} />
            <span>06 79 39 34 29</span>
          </a>
          <div style={contactRowStyle}>
            <MapPin style={{ width: 15, height: 15, color: GOLD, flexShrink: 0 }} />
            <span>Île-de-France</span>
          </div>
        </div>

        {/* Col 4 — Suivez-nous */}
        <div>
          <p style={columnTitleStyle}>Suivez-nous</p>
          <div style={{ display: "flex", gap: "1rem" }}>
            <a href="https://instagram.com/selfieboothevent" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" style={socialLinkStyle}>
              <Instagram style={{ width: 24, height: 24 }} />
            </a>
            <a href="https://facebook.com/selfieboothevent" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" style={socialLinkStyle}>
              <Facebook style={{ width: 24, height: 24 }} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="footer-bottom"
        style={{
          borderTop: `1px solid ${BORDER}`,
          marginTop: "2.5rem",
          paddingTop: "1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <p style={{ color: TEXT_MUTED, fontSize: "0.82rem", margin: 0 }}>
          © {new Date().getFullYear()} SelfieBooth Event · SIRET 931 457 360 00010
        </p>
        <a
          href="#gerer-cookies"
          onClick={openCookieBanner}
          style={{ ...linkStyle, cursor: "pointer", fontSize: "0.82rem" }}
          className="footer-link"
          data-testid="footer-manage-cookies"
        >
          Gérer mes cookies
        </a>
      </div>
    </div>

    <style>{`
      @media (max-width: 767px) {
        .footer-grid { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
        .footer-bottom { flex-direction: column !important; text-align: center !important; }
      }
      footer .footer-link:hover { text-decoration: underline; color: ${GOLD} !important; }
      footer .footer-link:hover svg { color: ${GOLD} !important; }
    `}</style>
  </footer>
);

export default Footer;
